const styles = {
  INFO: "color: #3B82F6; font-weight: bold;",
  SUCCESS: "color: #10B981; font-weight: bold;",
  WARN: "color: #F59E0B; font-weight: bold;",
  ERROR: "color: #EF4444; font-weight: bold;"
};
const createLogEntry = (level, message, additionalData, includeStack = false) => {
  const timestamp = (/* @__PURE__ */ new Date()).toISOString();
  let logMessage;
  let logData = additionalData;
  if (typeof message === "string") {
    logMessage = message;
  } else if (typeof message === "object" && message !== null) {
    logMessage = "Object logged";
    logData = message;
  } else {
    logMessage = String(message);
  }
  const entry = {
    timestamp,
    level,
    message: logMessage
  };
  if (logData !== void 0) {
    entry.data = logData;
  }
  if (includeStack) {
    const stack = new Error().stack;
    if (stack) {
      entry.stack = stack.split("\n").slice(3).join("\n");
    }
  }
  if (typeof window !== "undefined") {
    entry.context = {
      url: window.location.href,
      userAgent: navigator.userAgent,
      viewport: {
        width: window.innerWidth,
        height: window.innerHeight
      }
    };
  }
  return entry;
};
const logWithStyle = (consoleMethod, level, entry) => {
  console[consoleMethod](`%c[${level}]`, styles[level], entry);
};
const logger = {
  info: (message, data) => {
    const entry = createLogEntry("INFO", message, data);
    logWithStyle("log", "INFO", entry);
  },
  success: (message, data) => {
    const entry = createLogEntry("SUCCESS", message, data);
    logWithStyle("log", "SUCCESS", entry);
  },
  warn: (message, data) => {
    const entry = createLogEntry("WARN", message, data, true);
    logWithStyle("warn", "WARN", entry);
  },
  error: (message, data) => {
    const entry = createLogEntry("ERROR", message, data, true);
    logWithStyle("error", "ERROR", entry);
  }
};
const ACTIVE_CLASS = "calendar__lightbox-item--active";
const THUMB_SELECTOR = "[data-calendar-thumb]";
const ITEM_SELECTOR = "[data-lightbox-item]";
const DIALOG_SELECTOR$1 = "[data-lightbox-dialog]";
const INDEX_SELECTOR = "[data-lightbox-index]";
class CalendarLightbox extends HTMLElement {
  dialog = null;
  items = [];
  thumbs = [];
  current = 0;
  connectedCallback() {
    this.dialog = this.querySelector(DIALOG_SELECTOR$1);
    this.items = Array.from(this.querySelectorAll(ITEM_SELECTOR));
    this.thumbs = Array.from(this.querySelectorAll(THUMB_SELECTOR));
    if (!this.dialog || this.items.length === 0) {
      return;
    }
    this.addEventListener("click", this.handleClick);
    this.addEventListener("keydown", this.handleKeydown);
    this.dialog.addEventListener("close", this.handleClose);
  }
  disconnectedCallback() {
    this.removeEventListener("click", this.handleClick);
    this.removeEventListener("keydown", this.handleKeydown);
    this.dialog?.removeEventListener("close", this.handleClose);
  }
  handleClick = (event) => {
    const target = event.target;
    if (!target) {
      return;
    }
    const thumb = target.closest(THUMB_SELECTOR);
    if (thumb) {
      this.open(this.thumbs.indexOf(thumb));
      return;
    }
    if (target.closest("[data-lightbox-close]")) {
      this.dialog?.close();
      return;
    }
    if (target.closest("[data-lightbox-prev]")) {
      this.step(-1);
      return;
    }
    if (target.closest("[data-lightbox-next]")) {
      this.step(1);
      return;
    }
    if (target === this.dialog) {
      this.dialog.close();
    }
  };
  handleKeydown = (event) => {
    if (!this.dialog?.open) {
      return;
    }
    if (event.key === "ArrowRight") {
      event.preventDefault();
      this.step(1);
      return;
    }
    if (event.key === "ArrowLeft") {
      event.preventDefault();
      this.step(-1);
    }
  };
  /** Opens the viewer on one slide, and remembers which thumb sent us there. */
  open = (index) => {
    if (index < 0 || !this.dialog) {
      logger.warn("Calendar lightbox opened from an unknown thumbnail", { index });
      return;
    }
    this.show(index);
    this.dialog.showModal();
  };
  /** Moves by one, wrapping at both ends so the set is a loop. */
  step = (delta) => {
    const count = this.items.length;
    this.show((this.current + delta + count) % count);
  };
  show = (index) => {
    this.pauseVideos();
    this.items.forEach((item, i) => item.classList.toggle(ACTIVE_CLASS, i === index));
    this.current = index;
    const counter = this.querySelector(INDEX_SELECTOR);
    if (counter) {
      counter.textContent = String(index + 1);
    }
  };
  handleClose = () => {
    this.pauseVideos();
    this.thumbs[this.current]?.focus();
  };
  pauseVideos = () => {
    this.querySelectorAll("video").forEach((video) => video.pause());
  };
}
const TEXT_SELECTOR = "[data-readmore-text]";
const BUTTON_SELECTOR = "[data-readmore-open]";
const DIALOG_SELECTOR = "[data-readmore-dialog]";
class CalendarReadMore extends HTMLElement {
  text = null;
  button = null;
  dialog = null;
  observer = null;
  connectedCallback() {
    this.text = this.querySelector(TEXT_SELECTOR);
    this.button = this.querySelector(BUTTON_SELECTOR);
    this.dialog = this.querySelector(DIALOG_SELECTOR);
    if (!this.text || !this.button || !this.dialog) {
      return;
    }
    this.addEventListener("click", this.handleClick);
    this.observer = new ResizeObserver(this.syncButton);
    this.observer.observe(this.text);
  }
  disconnectedCallback() {
    this.removeEventListener("click", this.handleClick);
    this.observer?.disconnect();
    this.observer = null;
  }
  /**
   * Shows the button only when the clamp is actually hiding something. The 1px
   * tolerance is for sub-pixel line heights, which otherwise report a clamped
   * overflow on copy that fits exactly.
   */
  syncButton = () => {
    if (!this.text || !this.button) {
      return;
    }
    const clipped = this.text.scrollHeight - this.text.clientHeight > 1;
    this.button.hidden = !clipped;
  };
  handleClick = (event) => {
    const target = event.target;
    if (!target || !this.dialog) {
      return;
    }
    if (target.closest(BUTTON_SELECTOR)) {
      this.dialog.showModal();
      return;
    }
    if (target.closest("[data-readmore-close]")) {
      this.dialog.close();
      return;
    }
    if (target === this.dialog) {
      this.dialog.close();
    }
  };
}
class StorageHelper {
  storage = null;
  type;
  isAvailable = false;
  constructor(type) {
    this.type = type;
    this.checkAvailability();
  }
  /**
   * Check if storage is available (handles private browsing, etc.)
   */
  checkAvailability() {
    try {
      const storage = window[this.type];
      const testKey = "__storage_test__";
      storage.setItem(testKey, "test");
      storage.removeItem(testKey);
      this.storage = storage;
      this.isAvailable = true;
    } catch (e) {
      this.isAvailable = false;
      logger.warn(`${this.type} is not available`, { error: e });
    }
  }
  /**
   * Set an item in storage
   */
  set(key, value, expiresInMinutes) {
    if (!this.isAvailable || !this.storage) return false;
    try {
      const item = { value };
      if (expiresInMinutes) {
        item.expires = Date.now() + expiresInMinutes * 60 * 1e3;
      }
      this.storage.setItem(key, JSON.stringify(item));
      return true;
    } catch (e) {
      logger.error("Failed to set storage item", { key, error: e });
      return false;
    }
  }
  /**
   * Get an item from storage
   */
  get(key) {
    if (!this.isAvailable || !this.storage) return null;
    try {
      const raw = this.storage.getItem(key);
      if (!raw) return null;
      const item = JSON.parse(raw);
      if (item.expires && Date.now() > item.expires) {
        this.remove(key);
        return null;
      }
      return item.value;
    } catch (e) {
      logger.error("Failed to get storage item", { key, error: e });
      return null;
    }
  }
  /**
   * Remove an item from storage
   */
  remove(key) {
    if (!this.isAvailable || !this.storage) return false;
    try {
      this.storage.removeItem(key);
      return true;
    } catch (e) {
      logger.error("Failed to remove storage item", { key, error: e });
      return false;
    }
  }
  /**
   * Clear all items from storage
   */
  clear() {
    if (!this.isAvailable || !this.storage) return false;
    try {
      this.storage.clear();
      return true;
    } catch (e) {
      logger.error("Failed to clear storage", { error: e });
      return false;
    }
  }
  /**
   * Check if a key exists in storage
   */
  has(key) {
    if (!this.isAvailable || !this.storage) return false;
    return this.storage.getItem(key) !== null;
  }
  /**
   * Get all keys in storage
   */
  keys() {
    if (!this.isAvailable || !this.storage) return [];
    const keys = [];
    for (let i = 0; i < this.storage.length; i++) {
      const key = this.storage.key(i);
      if (key) keys.push(key);
    }
    return keys;
  }
  /**
   * Get storage size in bytes (approximate)
   */
  size() {
    if (!this.isAvailable || !this.storage) return 0;
    let total = 0;
    for (let i = 0; i < this.storage.length; i++) {
      const key = this.storage.key(i);
      if (key) {
        const value = this.storage.getItem(key);
        if (value) {
          total += key.length + value.length;
        }
      }
    }
    return total;
  }
  /**
   * Create a namespaced storage helper
   * Prefixes all keys with the namespace
   */
  namespace(prefix) {
    return new NamespacedStorage(this, prefix);
  }
  /**
   * Check if storage is available
   */
  get available() {
    return this.isAvailable;
  }
}
class NamespacedStorage {
  constructor(storage, prefix) {
    this.storage = storage;
    this.prefix = prefix;
  }
  getKey(key) {
    return `${this.prefix}:${key}`;
  }
  set(key, value, expiresInMinutes) {
    return this.storage.set(this.getKey(key), value, expiresInMinutes);
  }
  get(key) {
    return this.storage.get(this.getKey(key));
  }
  remove(key) {
    return this.storage.remove(this.getKey(key));
  }
  has(key) {
    return this.storage.has(this.getKey(key));
  }
  /**
   * Clear all items in this namespace
   */
  clear() {
    const keys = this.storage.keys().filter((key) => key.startsWith(`${this.prefix}:`));
    let success = true;
    keys.forEach((key) => {
      if (!this.storage.remove(key)) {
        success = false;
      }
    });
    return success;
  }
  /**
   * Get all keys in this namespace (without prefix)
   */
  keys() {
    const prefixLength = this.prefix.length + 1;
    return this.storage.keys().filter((key) => key.startsWith(`${this.prefix}:`)).map((key) => key.substring(prefixLength));
  }
}
const local = new StorageHelper("localStorage");
new StorageHelper("sessionStorage");
const OPENED_CLASS = "calendar__tile--opened";
const SHAKE_CLASS = "calendar__tile--shake";
const NO_ANIM_CLASS = "calendar__tile--no-anim";
const LOCKED_CLASS = "calendar__tile--locked";
const OPENED_DAY_CLASS = "calendar__day--opened";
const REVEAL_CLASS = "calendar__day--revealing";
const STORAGE_NAMESPACE = "calendar";
const OPENED_KEY = "opened-days";
const TILE_SELECTOR = "[data-calendar-tile]";
const PROMPT_SELECTOR = "[data-calendar-prompt]";
const COUNT_SELECTOR = "[data-calendar-opened]";
const GRID_SELECTOR = "[data-calendar-grid]";
const TO_GRID_SELECTOR = "[data-calendar-to-grid]";
const TO_GRID_VISIBLE_CLASS = "calendar__to-grid--visible";
const DOOR_TIMEOUT_MS = 700;
const DOOR_PROPERTIES = ["transform", "opacity"];
class CalendarSection extends HTMLElement {
  tiles = [];
  opened = /* @__PURE__ */ new Set();
  storageKey = STORAGE_NAMESPACE;
  prefersReducedMotion = false;
  gridObserver = null;
  connectedCallback() {
    this.tiles = Array.from(this.querySelectorAll(TILE_SELECTOR));
    if (this.tiles.length === 0) {
      return;
    }
    this.prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const calendarKey = this.dataset.calendarKey ?? "default";
    this.storageKey = `${STORAGE_NAMESPACE}:${calendarKey}`;
    this.opened = new Set(this.readOpened());
    this.restoreOpened();
    this.updateCount();
    this.addEventListener("click", this.handleTileClick);
    this.addEventListener("click", this.handlePromptClick);
    this.addEventListener("click", this.handleToGridClick);
    window.addEventListener("hashchange", this.handleHashChange);
    this.watchGrid();
    this.handleHashChange();
  }
  disconnectedCallback() {
    this.removeEventListener("click", this.handleTileClick);
    this.removeEventListener("click", this.handlePromptClick);
    this.removeEventListener("click", this.handleToGridClick);
    window.removeEventListener("hashchange", this.handleHashChange);
    this.gridObserver?.disconnect();
    this.gridObserver = null;
  }
  /**
   * Shows the floating button only once the grid has scrolled out of view.
   *
   * An observer rather than a scroll listener: it fires only when the panel
   * actually crosses the edge of the viewport, instead of on every frame of
   * every scroll.
   */
  watchGrid = () => {
    const grid = this.querySelector(GRID_SELECTOR);
    const button = this.querySelector(TO_GRID_SELECTOR);
    if (!grid || !button) {
      return;
    }
    this.gridObserver = new IntersectionObserver(([entry]) => {
      button.classList.toggle(TO_GRID_VISIBLE_CLASS, !entry.isIntersecting);
    });
    this.gridObserver.observe(grid);
  };
  /** Takes the visitor back up to the grid. */
  handleToGridClick = (event) => {
    const target = event.target;
    if (!target?.closest(TO_GRID_SELECTOR)) {
      return;
    }
    this.querySelector(GRID_SELECTOR)?.scrollIntoView({
      behavior: this.prefersReducedMotion ? "auto" : "smooth",
      block: "start"
    });
  };
  readOpened = () => {
    if (!local.available) {
      return [];
    }
    const stored = local.namespace(this.storageKey).get(OPENED_KEY);
    return Array.isArray(stored) ? stored : [];
  };
  persistOpened = () => {
    if (!local.available) {
      return;
    }
    local.namespace(this.storageKey).set(OPENED_KEY, Array.from(this.opened));
  };
  getTile = (day) => this.tiles.find((tile) => tile.dataset.day === day);
  getBlock = (day) => {
    const block = document.getElementById(`day-${day}`);
    return block && this.contains(block) ? block : null;
  };
  /**
   * True when the day has a tile that is not date-locked — i.e. when its content
   * is actually present in the page. A day that has since re-locked still has a
   * tile, so the locked class is the test, never mere existence.
   */
  isOpenable = (tile) => tile !== void 0 && !tile.classList.contains(LOCKED_CLASS);
  /** Re-opens doors from a previous visit, with no animation replay. */
  restoreOpened = () => {
    this.opened.forEach((day) => {
      const tile = this.getTile(day);
      if (!this.isOpenable(tile)) {
        return;
      }
      tile.classList.add(NO_ANIM_CLASS, OPENED_CLASS);
      void tile.offsetWidth;
      tile.classList.remove(NO_ANIM_CLASS);
      this.getBlock(day)?.classList.add(OPENED_DAY_CLASS);
    });
  };
  /** Writes the opened tally into the panel's meta row. */
  updateCount = () => {
    const target = this.querySelector(COUNT_SELECTOR);
    if (!target) {
      return;
    }
    const count = this.tiles.filter((tile) => {
      const day = tile.dataset.day;
      return day !== void 0 && this.opened.has(day);
    }).length;
    target.textContent = String(count);
  };
  markOpened = (day) => {
    if (this.opened.has(day)) {
      return;
    }
    this.opened.add(day);
    this.persistOpened();
    this.updateCount();
  };
  handleTileClick = (event) => {
    const target = event.target;
    const tile = target?.closest(TILE_SELECTOR);
    if (!tile) {
      return;
    }
    const day = tile.dataset.day;
    if (!day) {
      return;
    }
    event.preventDefault();
    if (tile.classList.contains(LOCKED_CLASS)) {
      this.refuse(tile);
      return;
    }
    if (tile.classList.contains(OPENED_CLASS)) {
      this.revealDay(day);
      return;
    }
    this.openDay({ day, tile });
  };
  /**
   * Opens a day from the placeholder sitting where its content will be, rather
   * than from its door up in the grid.
   *
   * Only ever rendered for a date-unlocked day, so this is not a second gate —
   * it reaches exactly the content the server already sent. The date gate is
   * still the only gate.
   */
  handlePromptClick = (event) => {
    const target = event.target;
    const prompt = target?.closest(PROMPT_SELECTOR);
    if (!prompt) {
      return;
    }
    const day = prompt.dataset.day;
    if (!day) {
      return;
    }
    event.preventDefault();
    const tile = this.getTile(day);
    if (this.isOpenable(tile)) {
      this.markOpened(day);
      tile.classList.add(OPENED_CLASS);
    }
    this.revealDay(day);
  };
  /** Shakes a locked door. Deliberately does not scroll or reveal anything. */
  refuse = (tile) => {
    if (this.prefersReducedMotion || tile.classList.contains(SHAKE_CLASS)) {
      return;
    }
    tile.classList.add(SHAKE_CLASS);
    tile.addEventListener("animationend", () => tile.classList.remove(SHAKE_CLASS), { once: true });
  };
  /**
   * Opens the door, then scrolls to the day once it has finished opening.
   *
   * The wait is deliberate in both motion modes: under reduced motion the door
   * cross-fades rather than swinging, and the scroll still waits for that, so
   * opening a gate never reads as the page jumping out from under the click.
   */
  openDay = ({ day, tile }) => {
    this.markOpened(day);
    tile.classList.add(OPENED_CLASS);
    const door = tile.querySelector(".calendar__tile-door");
    if (!door) {
      this.revealDay(day);
      return;
    }
    let done = false;
    const once = () => {
      if (done) {
        return;
      }
      done = true;
      this.revealDay(day);
    };
    const onTransitionEnd = (event) => {
      if (event.target !== door || !DOOR_PROPERTIES.includes(event.propertyName)) {
        return;
      }
      door.removeEventListener("transitionend", onTransitionEnd);
      once();
    };
    door.addEventListener("transitionend", onTransitionEnd);
    window.setTimeout(once, DOOR_TIMEOUT_MS);
  };
  /** Scrolls a day into view, plays its arrival flourish and moves focus there. */
  revealDay = (day) => {
    const block = this.getBlock(day);
    if (!block) {
      logger.warn("Calendar day block not found", { day });
      return;
    }
    block.classList.add(OPENED_DAY_CLASS);
    block.scrollIntoView({
      behavior: this.prefersReducedMotion ? "auto" : "smooth",
      block: "start"
    });
    this.playReveal(block);
    block.focus({ preventScroll: true });
    window.history.replaceState(null, "", `#day-${day}`);
  };
  /**
   * Animates a day block into place. The content is always in the page, so this
   * is a flourish on arrival rather than a disclosure — which is why it has to be
   * restartable: the same day can be opened from its tile any number of times.
   */
  playReveal = (block) => {
    block.classList.remove(REVEAL_CLASS);
    void block.offsetWidth;
    block.classList.add(REVEAL_CLASS);
    block.addEventListener("animationend", () => block.classList.remove(REVEAL_CLASS), { once: true });
  };
  handleHashChange = () => {
    const match = /^#day-(.+)$/.exec(window.location.hash);
    if (!match) {
      return;
    }
    const day = decodeURIComponent(match[1]);
    const tile = this.getTile(day);
    if (!this.isOpenable(tile)) {
      this.getBlock(day)?.focus({ preventScroll: false });
      return;
    }
    if (!tile.classList.contains(OPENED_CLASS)) {
      this.markOpened(day);
      tile.classList.add(OPENED_CLASS);
    }
    this.revealDay(day);
  };
}
customElements.define("calendar-section", CalendarSection);
customElements.define("calendar-lightbox", CalendarLightbox);
customElements.define("calendar-readmore", CalendarReadMore);
